import {
  put,
  call,
  takeLatest,
  takeEvery,
  spawn,
  select
} from 'redux-saga/effects';
import {
  ADMIN_ORG_FETCH_ALL,
  ADMIN_ORG_FETCH,
  ADMIN_ORG_ADD,
  ADMIN_ORG_UPDATE,
  ADMIN_ORG_DELETE,
  ADMIN_ORG_ADD_USER
} from './constants';
import {
  fetchOrgsSuccess,
  fetchOrgsFail,
  fetchOrg as fetchOrgAction,
  fetchOrgSuccess,
  fetchOrgFail,
  addOrgFail,
  addOrgSucccess,
  updateOrgSuccess,
  updateOrgFail,
  deleteOrgSuccess,
  deleteOrgFail,
  addOrgUserFail,
  addOrgUserSuccess
} from './actions';
import { parseApiError } from '../../util/api';
import OrgAdminAPI from './OrgAdminAPI';
import AdminAPI from '../AdminAPI';
import { showNotification } from '../../notifications/actions';
import { isValidOrg } from './util';
import { getOrgUnderEdit } from './reducer';

function *fetchOrgs () {
  try {
    const { data } = yield call(OrgAdminAPI.fetchOrgs);
    if (data && Array.isArray(data.orgs)) {
      yield put(fetchOrgsSuccess(data.orgs));
    } else {
      let msg = 'unexpected response fetching organisations';
      yield put(fetchOrgsFail(msg));
      yield put(showNotification(
        'Failed to load organisations', msg, 'error', 10
      ));
    }
  } catch (error) {
    const msg = parseApiError(error);
    yield put(fetchOrgsFail(msg));
    yield put(showNotification(
      'Failed to load organisations', msg, 'error', 10
    ));
  }
}

function *fetchOrg (action) {
  try {
    const { data } = yield call(OrgAdminAPI.fetchOrg, action.orgId);
    if (data && data.org) {
      const orgUnderEdit = yield select(getOrgUnderEdit);
      if (
        orgUnderEdit &&
        orgUnderEdit.bcmsaccount &&
        data.org.bcmsaccount &&
        data.org.bcmsaccount.lastSync &&
        data.org.bcmsaccount.lastSync !== orgUnderEdit.bcmsaccount.lastSync) {
        if (data.org.bcmsaccount.error) {
          yield put(showNotification(
            'BCMS sync failed',
            `${data.org.name}: ${data.org.bcmsaccount.error}`,
            'error',
            10
          ));
        } else if (data.org.bcmsaccount.lastResult) {
          yield put(showNotification(
            'BCMS sync complete',
            `${data.org.name}: ${data.org.bcmsaccount.lastResult}`,
            'info',
            10
          ));
        }
      }
      yield put(fetchOrgSuccess(data.org));
    } else {
      let msg = 'unexpected response fetching organisation';
      yield put(fetchOrgFail(msg));
      yield put(showNotification(
        'Failed to load organisation', msg, 'error', 10
      ));
    }
  } catch (error) {
    const msg = parseApiError(error);
    yield put(fetchOrgFail(msg));
    yield put(showNotification(
      'Failed to load organisation', msg, 'error', 10
    ));
  }
}

function *addOrg (action) {
  try {
    if (!isValidOrg(action.org)) return;
    yield call(OrgAdminAPI.addOrg, action.org);
    yield put(addOrgSucccess(action.org));
    yield put(showNotification(
      'Success', `${action.org.name} saved`, 'info', 5
    ));
  } catch (error) {
    const msg = parseApiError(error);
    yield put(addOrgFail(msg, action.org));
    yield put(showNotification(
      `Failed to add organisation ${action.org.name} `,
      msg,
      'error',
      10
    ));
  }
}

function *deleteS3Files (files) {
  try {
    if (files.length < 1) return;
    yield call(AdminAPI.deleteDocuments, { files });
  } catch (error) {
    console.log(`error deleting documents. ${error.toString()}`);
  }
}

function *updateOrg (action) {
  try {
    if (!isValidOrg(action.org)) return;
    const { users, ...org } = action.org;
    yield call(OrgAdminAPI.updateOrg, org);
    yield spawn(deleteS3Files, action.cloudFilesToRemove);
    yield put(updateOrgSuccess(action.org));
    yield put(showNotification(
      'Success', `${action.org.name} saved`, 'info', 5
    ));
  } catch (error) {
    const msg = parseApiError(error);
    yield put(updateOrgFail(msg, action.org));
    yield put(showNotification(
      `Failed to update organisation ${action.org.name}`,
      msg,
      'error',
      10
    ));
  }
}

function *deleteOrg (action) {
  try {
    yield call(OrgAdminAPI.deleteOrg, action.org);
    yield spawn(deleteS3Files, action.cloudFilesToRemove);
    yield put(deleteOrgSuccess(action.org));
    yield put(showNotification(
      'Success', `${action.org.name} deleted`, 'info', 5
    ));
  } catch (error) {
    const msg = parseApiError(error);
    yield put(deleteOrgFail(msg, action.org));
    yield put(showNotification(
      `Failed to delete organisation ${action.org.name}`,
      msg,
      'error',
      10
    ));
  }
}

function *addUser (action) {
  try {
    const { data } = yield call(
      OrgAdminAPI.addUser, action.email, action.orgId
    );
    yield put(addOrgUserSuccess(data.user, action.orgId));
    yield put(showNotification(
      `Added ${data.user.name || data.user.email}`,
      `${data.user.name || data.user.email} must login again for access`,
      'success',
      5
    ));
    yield put(fetchOrgAction(action.orgId));
  } catch (error) {
    const msg = parseApiError(error);
    yield put(addOrgUserFail(msg));
    yield put(showNotification(
      `Failed to add user ${action.email}`,
      msg,
      'error',
      10
    ));
  }
}

function *adminOrgSagas () {
  yield takeLatest(ADMIN_ORG_FETCH_ALL, fetchOrgs);
  yield takeLatest(ADMIN_ORG_FETCH, fetchOrg);
  yield takeEvery(ADMIN_ORG_ADD, addOrg);
  yield takeEvery(ADMIN_ORG_UPDATE, updateOrg);
  yield takeEvery(ADMIN_ORG_DELETE, deleteOrg);
  yield takeLatest(ADMIN_ORG_ADD_USER, addUser);
}

export default adminOrgSagas;
